import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { PlatformUserStatus, type PlatformUser } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { toUpdateUserRequest } from '../api/mappers';
import { useUpdateUser } from '../hooks/use-update-user';
import {
  editUserSchema,
  type EditUserInput,
  type EditUserOutput,
} from '../schemas/edit-user-schema';
import { assignableRoles, editErrorKey, editRestrictions, type Actor } from '../schemas/user-rules';

import { RoleSelect } from './role-select';

export interface EditUserDialogProps {
  actor: Actor;
  /** The row being edited; null closes the dialog. */
  user: PlatformUser | null;
  users: readonly PlatformUser[];
  onClose: () => void;
}

export function EditUserDialog({ actor, user, users, onClose }: EditUserDialogProps) {
  return (
    <Dialog
      open={user !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-md">
        {user ? (
          <EditForm key={user.id} actor={actor} user={user} users={users} onDone={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface EditFormProps {
  actor: Actor;
  user: PlatformUser;
  users: readonly PlatformUser[];
  onDone: () => void;
}

/**
 * Name, role and status in one form. Role and status lock for yourself and for the last active
 * owner; whatever the client could not know (a stale list) comes back as 422/403/409 and is
 * shown inline in the user's words.
 */
function EditForm({ actor, user, users, onDone }: EditFormProps) {
  const { t } = useTranslation(['users', 'common']);
  const update = useUpdateUser('users:toasts.updated');
  const restrictions = editRestrictions(actor, user, users);
  const roles = assignableRoles(actor.role);
  const form = useForm<EditUserInput, unknown, EditUserOutput>({
    resolver: useI18nResolver(editUserSchema),
    defaultValues: { name: user.name, role: user.role, status: user.status },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    const body = toUpdateUserRequest(user, values);
    if (Object.keys(body).length === 0) {
      onDone();
      return;
    }
    try {
      await update.mutateAsync({ userId: user.id, body });
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  const lockHint = restrictions.lockReason ? t(`users:locks.${restrictions.lockReason}`) : null;
  const serverErrorKey = update.isError ? editErrorKey(update.error) : null;

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <DialogHeader>
          <DialogTitle>{t('users:editDialog.title')}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        {update.isError && !update.error.hasFieldErrors ? (
          <ServerError error={update.error} message={serverErrorKey ? t(serverErrorKey) : null} />
        ) : null}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('users:fields.name')}</FormLabel>
              <FormControl>
                <Input autoComplete="off" autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('users:fields.role')}</FormLabel>
              <FormControl>
                <RoleSelect
                  value={field.value}
                  onChange={field.onChange}
                  roles={roles}
                  disabled={restrictions.roleLocked}
                />
              </FormControl>
              {restrictions.roleLocked && lockHint ? (
                <FormDescription>{lockHint}</FormDescription>
              ) : null}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('users:fields.status')}</FormLabel>
              <FormControl>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={restrictions.statusLocked}
                >
                  <SelectTrigger className="w-full" aria-label={t('users:fields.status')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(PlatformUserStatus).map((status) => (
                      <SelectItem key={status} value={status}>
                        {t(`users:status.${status}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                {restrictions.statusLocked && lockHint ? lockHint : t('users:fields.statusHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone} disabled={update.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
